/**
 * Admin "Nézet" view toggles (overlay highlight, dimension gizmo).
 *
 * Shared between the admin tab (ViewOptionsTab — the UI that flips them) and
 * UnityView (which re-applies them to the viewer after the model loads).
 *
 * Persistence = sessionStorage on purpose: the state survives a page reload
 * (so an admin testing the gizmos doesn't have to re-toggle every refresh) but
 * is cleared on browser restart / tab close — these are dev/preview switches,
 * not a saved preference.
 */
export interface ViewOption {
    id: string;
    label: string;
    /** Unity method on the 'WebInterface' game object, called with 'true'/'false'. */
    method: string;
}

export const VIEW_OPTIONS: ViewOption[] = [
    { id: 'overlayHighlight', label: 'Overlay kiemelés', method: 'SetOverlayHighlightEnabled' },
    { id: 'dimensionGizmo',   label: 'Méret gizmó',      method: 'SetDimensionGizmoEnabled' },
];

/** A one-shot view command: pressing it fires once, and nothing is stored. */
export interface ViewAction {
    id: string;
    label: string;
    /** Unity method on the 'WebInterface' game object. */
    method: string;
    /** The single string argument that method takes. */
    arg: string;
}

/**
 * Deliberately separate from VIEW_OPTIONS rather than another entry in it: that array is
 * what UnityView replays once the model has loaded, and every member of it is sent as a
 * 'true'/'false' string. A camera command has no on/off state to restore, so listing one
 * there would send it a meaningless 'true' on every reload and override whatever view the
 * user had moved to since.
 */
export const VIEW_ACTIONS: ViewAction[] = [
    { id: 'isotropicTopView', label: 'Isotropic Top View', method: 'SetViewMode', arg: 'TopLocked' },
];

const storageKey = (method: string): string => `ucview:${method}`;

export const readViewOption = (method: string): boolean => {
    try {
        return typeof sessionStorage !== 'undefined'
            && sessionStorage.getItem(storageKey(method)) === 'true';
    } catch {
        return false;
    }
};

export const writeViewOption = (method: string, enabled: boolean): void => {
    try {
        sessionStorage.setItem(storageKey(method), enabled ? 'true' : 'false');
    } catch {
        /* sessionStorage unavailable (private mode / disabled) — ignore. */
    }
};
